import type { SendIncompleteReason } from '@/model/Agent.model';
import { _notNil } from '@/littledash';
import { isAiFinding } from './SendAiUse';

/**
 * Whether a finding reports a check that could not be completed — skipped, or unable to decide some
 * comparisons — rather than something it found. The validator says so with a typed `disclosure`, and
 * nothing else does: an informational finding without one is a finding about the data.
 */
export const isIncompleteCheck = (finding: { disclosure?: SendIncompleteReason }): boolean =>
  _notNil(finding.disclosure);

/** What a finding needs for the report to tell where it belongs. */
interface PartitionableFinding {
  origin: 'deterministic' | 'llm';
  disclosure?: SendIncompleteReason;
}

/** One list of findings, split into the three things a SEND report shows apart. */
export interface PartitionedFindings<T extends PartitionableFinding> {
  /** What the checks found in the data. The only findings severity filters and finding counts cover. */
  checks: Array<T>;
  /** A check's notice that it could not decide something. Never a finding about the data. */
  disclosures: Array<T>;
  /** Benchling In Vivo AI notes, which the report shows in their own section and nowhere else. */
  aiNotes: Array<T>;
}

/**
 * Splits findings, in their order, into what the checks found, what a check could not decide, and
 * Benchling In Vivo AI notes, so no section shows one as another. An AI note goes to the AI notes
 * whatever else it carries; a disclosure is told apart by its typed reason, never by its severity.
 */
export const partitionFindings = <T extends PartitionableFinding>(findings: ReadonlyArray<T>): PartitionedFindings<T> =>
  findings.reduce<PartitionedFindings<T>>(
    (parts, finding) => {
      if (isAiFinding(finding)) {
        parts.aiNotes.push(finding);
      } else if (isIncompleteCheck(finding)) {
        parts.disclosures.push(finding);
      } else {
        parts.checks.push(finding);
      }
      return parts;
    },
    { checks: [], disclosures: [], aiNotes: [] }
  );
